/**
 * Optional guided tour of customer portal chrome (react-joyride).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import {
  PORTAL_TOUR_STEPS,
  hasCompletedPortalTour,
  markPortalTourCompleted,
} from "../tour/portalTour";

export const PortalTourContext = createContext({
  startTour: () => {},
  tourRunning: false,
  userKey: "",
});

export function usePortalTour() {
  return useContext(PortalTourContext);
}

const joyrideStyles = {
  options: {
    zIndex: 12000,
    primaryColor: "#2563eb",
    textColor: "#111827",
    backgroundColor: "#ffffff",
    overlayColor: "rgba(15, 23, 42, 0.45)",
    arrowColor: "#ffffff",
  },
  tooltip: {
    borderRadius: 12,
    padding: 16,
    fontSize: 14,
  },
  tooltipTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 6,
  },
  tooltipContent: {
    padding: "4px 0 0",
    lineHeight: 1.45,
  },
  buttonNext: {
    backgroundColor: "#2563eb",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 14px",
  },
  buttonBack: {
    color: "#374151",
    fontSize: 13,
    marginRight: 8,
  },
  buttonSkip: {
    color: "#6b7280",
    fontSize: 13,
  },
};

/**
 * @param {{
 *   userKey: string,
 *   blocking: boolean,
 *   noticesReady: boolean,
 *   noticesOverlayOpen: boolean,
 *   children: import("react").ReactNode,
 * }} props
 */
export default function PortalTourProvider({
  userKey,
  blocking,
  noticesReady,
  noticesOverlayOpen,
  children,
}) {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [autoOffered, setAutoOffered] = useState(false);

  const startTour = useCallback(() => {
    if (blocking) return;
    setStepIndex(0);
    setRun(true);
  }, [blocking]);

  useEffect(() => {
    if (autoOffered || blocking || !noticesReady || noticesOverlayOpen) return;
    if (!userKey) return;
    if (hasCompletedPortalTour(userKey)) {
      setAutoOffered(true);
      return;
    }
    const timer = setTimeout(() => {
      setStepIndex(0);
      setRun(true);
      setAutoOffered(true);
    }, 700);
    return () => clearTimeout(timer);
  }, [autoOffered, blocking, noticesReady, noticesOverlayOpen, userKey]);

  useEffect(() => {
    if (blocking && run) {
      setRun(false);
    }
  }, [blocking, run]);

  const handleJoyride = useCallback(
    (data) => {
      const { status, action, type, index } = data;

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
      }

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        setRun(false);
        setStepIndex(0);
        if (userKey) markPortalTourCompleted(userKey);
      }
    },
    [userKey]
  );

  const value = useMemo(
    () => ({ startTour, tourRunning: run, userKey: userKey || "" }),
    [startTour, run, userKey]
  );

  return (
    <PortalTourContext.Provider value={value}>
      {children}
      {!blocking ? (
        <Joyride
          steps={PORTAL_TOUR_STEPS}
          run={run}
          stepIndex={stepIndex}
          continuous
          showSkipButton
          showProgress
          disableScrolling
          disableOverlayClose
          scrollToFirstStep
          callback={handleJoyride}
          styles={joyrideStyles}
          locale={{
            back: "Back",
            close: "Close",
            last: "Done",
            next: "Next",
            skip: "Skip tour",
          }}
        />
      ) : null}
    </PortalTourContext.Provider>
  );
}
